import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { User, Building2, Plus, Search } from "lucide-react";

/**
 * Small dialog for picking OR creating a contact + role, then handing back to
 * the parent to append to the task's participants list.
 *
 * Steps:
 *  1. Search — user types, we live-fetch contacts filtered by name
 *  2. Result rows — pick one → prompts for role → onPicked({ contact, role })
 *  3. "+ Kreiraj novi kontakt" — inline form (name / kind / one tag), submit
 *     hits POST /api/contacts and the newly-created contact enters step 2.
 */
const KNOWN_TAGS = [
  "client", "supplier", "influencer", "freelancer", "photographer",
  "agency", "factory", "courier", "partner", "other",
];
const TAG_LABEL = {
  client: "klijent", supplier: "dobavljač", influencer: "influenser",
  freelancer: "freelancer", photographer: "fotograf", agency: "agencija",
  factory: "fabrika", courier: "kurir", partner: "partner", other: "ostalo",
};
const ROLE_OPTIONS = [
  { value: "assignee", label: "Izvršilac" },
  { value: "external_party", label: "Eksterni učesnik" },
  { value: "watcher", label: "Prati" },
];

export default function AddParticipantDialog({
  open,
  onOpenChange,
  onPicked,
  excludeContactIds = [],
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("search");   // "search" | "role" | "create"
  const [pickedContact, setPickedContact] = useState(null);
  const [role, setRole] = useState("external_party");
  // Inline create form
  const [newForm, setNewForm] = useState({ name: "", kind: "person", tag: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ(""); setResults([]); setStep("search");
      setPickedContact(null); setRole("external_party");
      setNewForm({ name: "", kind: "person", tag: "" });
    }
  }, [open]);

  useEffect(() => {
    if (!open || step !== "search") return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/contacts${q ? `?q=${encodeURIComponent(q)}` : ""}`);
        setResults((data || []).filter((c) => !excludeContactIds.includes(c.id)));
      } catch (e) { /* silent */ }
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, step, excludeContactIds]);

  const pick = (contact) => {
    setPickedContact(contact);
    // Suggested default role
    setRole(contact.kind === "internal_user" ? "assignee" : "external_party");
    setStep("role");
  };

  const confirm = () => {
    if (!pickedContact) return;
    onPicked?.({ contact: pickedContact, role });
    onOpenChange(false);
  };

  const createContact = async () => {
    if (!newForm.name.trim()) return toast.error("Unesi ime kontakta");
    setCreating(true);
    try {
      const payload = {
        name: newForm.name.trim(),
        kind: newForm.kind,
        tags: newForm.tag ? [newForm.tag] : [],
      };
      const { data } = await api.post("/contacts", payload);
      toast.success("Kontakt dodat");
      pick(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-participant-dialog"
        className="bg-white max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {step === "search" && "Dodaj učesnika"}
            {step === "role" && "Izaberi ulogu"}
            {step === "create" && "Novi kontakt"}
          </DialogTitle>
        </DialogHeader>

        {step === "search" && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-ink-muted" />
              <Input
                data-testid="participant-search-input"
                placeholder="Traži kontakt po imenu..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto border border-line rounded-md divide-y divide-line">
              {loading && <div className="text-xs text-ink-muted p-2">tražim...</div>}
              {!loading && results.length === 0 && (
                <div className="text-xs text-ink-muted p-3">
                  Nema rezultata{q ? ` za „${q}"` : ""}.
                </div>
              )}
              {results.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  data-testid={`participant-pick-${c.id}`}
                  onClick={() => pick(c)}
                  className="w-full flex items-center gap-2 text-left text-sm px-3 py-2 hover:bg-surface-secondary"
                >
                  {c.kind === "organization" ? (
                    <Building2 className="w-4 h-4 text-ink-muted shrink-0" />
                  ) : (
                    <User className="w-4 h-4 text-ink-muted shrink-0" />
                  )}
                  <span className="flex-1 min-w-0 truncate">
                    {c.name}
                    {(c.tags || []).map((t) => TAG_LABEL[t]).filter(Boolean).length > 0 && (
                      <span className="text-ink-muted">
                        {" · "}{(c.tags || []).map((t) => TAG_LABEL[t]).filter(Boolean).join(", ")}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setStep("create")}
              data-testid="participant-create-new-btn"
              className="w-full text-left text-sm text-forest hover:bg-surface-secondary rounded-md border border-dashed border-line px-3 py-2 inline-flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Kreiraj novi kontakt
            </button>
          </div>
        )}

        {step === "role" && pickedContact && (
          <div className="space-y-3">
            <div className="text-sm text-ink">
              Kontakt: <span className="font-semibold">{pickedContact.name}</span>
            </div>
            <div>
              <Label>Uloga na tasku</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="participant-role-select" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-ink-muted mt-1">
                Izvršilac vodi task. Prati vidi task ali ne izvršava. Eksterni učesnik je druga strana s kojom se komunicira.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <button className="btn-ghost" onClick={() => setStep("search")}>Nazad</button>
              <button className="btn-primary" onClick={confirm} data-testid="participant-confirm-btn">
                Dodaj
              </button>
            </div>
          </div>
        )}

        {step === "create" && (
          <div className="space-y-3">
            <div>
              <Label>Ime</Label>
              <Input
                data-testid="new-contact-name-input"
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="npr. Aleksa Fotograf"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tip</Label>
                <Select value={newForm.kind} onValueChange={(v) => setNewForm({ ...newForm, kind: v })}>
                  <SelectTrigger data-testid="new-contact-kind-select" className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="person">Osoba</SelectItem>
                    <SelectItem value="organization">Kompanija</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vrsta (opciono)</Label>
                <Select value={newForm.tag || "none"} onValueChange={(v) => setNewForm({ ...newForm, tag: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="new-contact-tag-select" className="bg-white">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">—</SelectItem>
                    {KNOWN_TAGS.map((t) => (
                      <SelectItem key={t} value={t}>{TAG_LABEL[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button className="btn-ghost" onClick={() => setStep("search")}>Nazad</button>
              <button
                className="btn-primary"
                onClick={createContact}
                disabled={creating}
                data-testid="new-contact-save-btn"
              >
                {creating ? "Čuvam..." : "Sačuvaj i dodaj"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
